import { useEffect, useState } from "react";

import {
  AnchoringSection,
  BuoyageSection,
  AstroSection,
  ChartworkSection,
  CompassSection,
  CombosSection,
  DayShapesSection,
  DistressSection,
  CommsSection,
  EngineSection,
  FlagsSection,
  MnemonicsSection,
  SafetySection,
  GpsSection,
  IrpcsSection,
  KnotsSection,
  LightsNotationSection,
  MobSection,
  MorseSection,
  NatoSection,
  NavLightsSection,
  PilotageSection,
  PortSignalsSection,
  SailingSection,
  SemaphoreSection,
  type Section,
  TidesSection,
  WeatherSection,
} from "../pages/Reference";

interface Props {
  /// Which Reference section to display. When null, the panel is
  /// closed; the parent controls open/close via this prop.
  section: Section | null;
  /// Called when the user dismisses the panel (backdrop tap, Esc key,
  /// or the close button).
  onClose: () => void;
}

const SECTION_LABELS: Record<Section, string> = {
  morse: "Morse code",
  semaphore: "Semaphore",
  flags: "ICS flags",
  combos: "Flag combinations",
  "nav-lights": "Nav lights",
  "day-shapes": "Day shapes",
  buoyage: "IALA buoyage",
  "port-signals": "Port traffic signals",
  "lights-notation": "Light notation",
  irpcs: "IRPCS rules",
  weather: "Met Office weather",
  distress: "Distress signals",
  nato: "NATO phonetic",
  chartwork: "Chartwork",
  compass: "Compass error",
  astro: "Astronavigation",
  knots: "Knots",
  anchoring: "Anchoring",
  gps: "GPS literacy",
  tides: "Tides",
  pilotage: "Pilotage",
  sailing: "Sailing",
  mob: "MOB recovery",
  engine: "Engine — WOBBLE",
  mnemonics: "Mnemonics",
  comms: "VHF + SRC",
  safety: "Safety + first aid",
  predeparture: "Pre-departure + provisioning",
  powerboat: "Powerboat handling",
  passage: "Passage planning",
  ropework: "Ropework + sail repair",
  tender: "Tender handling",
};

function SectionContent({ section }: { section: Section }) {
  switch (section) {
    case "morse": return <MorseSection />;
    case "semaphore": return <SemaphoreSection />;
    case "flags": return <FlagsSection />;
    case "combos": return <CombosSection />;
    case "nav-lights": return <NavLightsSection />;
    case "day-shapes": return <DayShapesSection />;
    case "buoyage": return <BuoyageSection />;
    case "port-signals": return <PortSignalsSection />;
    case "lights-notation": return <LightsNotationSection />;
    case "irpcs": return <IrpcsSection />;
    case "weather": return <WeatherSection />;
    case "distress": return <DistressSection />;
    case "nato": return <NatoSection />;
    case "chartwork": return <ChartworkSection />;
    case "compass": return <CompassSection />;
    case "astro": return <AstroSection />;
    case "knots": return <KnotsSection />;
    case "anchoring": return <AnchoringSection />;
    case "gps": return <GpsSection />;
    case "tides": return <TidesSection />;
    case "pilotage": return <PilotageSection />;
    case "sailing": return <SailingSection />;
    case "mob": return <MobSection />;
    case "engine": return <EngineSection />;
    case "mnemonics": return <MnemonicsSection />;
    case "comms": return <CommsSection />;
    case "safety": return <SafetySection />;
    case "predeparture":
    case "powerboat":
    case "passage":
    case "ropework":
    case "tender":
      // These sections don't host help-drawer content yet — open the
      // full Reference picker instead.
      return (
        <p className="small muted">
          Open the relevant section in the Reference picker for full
          details.
        </p>
      );
  }
}

/// In-game help drawer. Slides up from the bottom on mobile, renders
/// as a centered sheet on desktop. Contains the same Reference
/// section the user would see at /play/reference — no duplication.
///
/// Open via `<HelpPanel section="morse" onClose={...} />`; close by
/// passing `section={null}` or via the panel's own close affordances
/// (backdrop tap, Esc, ✕ button).
export function HelpPanel({ section, onClose }: Props) {
  // Close on Esc — desktop-friendly affordance. Also lock body scroll
  // while open so the panel can scroll independently.
  useEffect(() => {
    if (!section) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [section, onClose]);

  if (!section) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Reference: ${SECTION_LABELS[section]}`}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        zIndex: 100,
      }}
    >
      <div
        // Stop backdrop-click propagation so taps inside the sheet
        // don't dismiss it.
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg, #fff)",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 -10px 30px rgba(0,0,0,0.15)",
          // On wider screens, cap the width and center horizontally so
          // we don't span an absurdly wide desktop viewport.
          maxWidth: 720,
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* Sticky header with the section name + close button. */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 16px",
            borderBottom: "1px solid var(--border)",
            position: "sticky",
            top: 0,
            background: "var(--bg-card, #fff)",
            zIndex: 1,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              aria-hidden
              style={{
                width: 36,
                height: 4,
                background: "var(--border-strong, #cbd5e1)",
                borderRadius: 2,
              }}
            />
            <h3 style={{ margin: 0, fontSize: 16 }}>
              Reference · {SECTION_LABELS[section]}
            </h3>
          </div>
          <button
            onClick={onClose}
            aria-label="Close reference"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
              padding: 4,
              color: "var(--text)",
            }}
          >
            ✕
          </button>
        </header>

        {/* Scrollable content area. */}
        <div
          style={{
            padding: "12px 16px 24px",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <SectionContent section={section} />
        </div>
      </div>
    </div>
  );
}

/// Companion trigger button — drop this in the corner of any game
/// page. Calls `onOpen()` which the parent typically sets to a
/// useState setter that swaps the HelpPanel `section` prop from null
/// to the relevant slug.
export function HelpButton({
  onOpen,
  label = "Reference",
}: {
  onOpen: () => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="secondary"
      style={{ fontSize: 13, padding: "6px 12px" }}
      aria-label="Open reference for this game"
    >
      ? {label}
    </button>
  );
}

/// Inline reference panel for beginner mode — a "cheat sheet" the
/// user can keep visible while drilling. Renders an expand/collapse
/// toggle plus, when expanded, the matching Reference section below
/// the game UI. Distinct from HelpPanel (full-overlay drawer for
/// occasional lookups). Default collapsed so it doesn't crowd the
/// screen until the user opts in.
///
/// Game pages should gate rendering on `difficulty === "beginner"` —
/// intermediate / expert tiers don't show this so users can't cheat
/// on harder difficulties.
export function InlineReference({ section }: { section: Section }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="secondary"
        style={{
          width: "100%",
          textAlign: "left",
          fontSize: 14,
          padding: "8px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        aria-expanded={open}
      >
        <span>
          {open ? "Hide reference panel" : "Show reference panel (cheat sheet)"}
        </span>
        <span aria-hidden style={{ fontSize: 12, opacity: 0.6 }}>
          {open ? "▲" : "▼"}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          <SectionContent section={section} />
        </div>
      )}
    </div>
  );
}
